import CtaIcon from '../ui/CtaIcon';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { VehicleHistoryEntry } from '../../types/vehicleHistory';
import { BuildPassportWorkspace } from './BuildPassportWorkspace';

const TYPE_META: Record<
  VehicleHistoryEntry['event_type'],
  { label: React.ReactNode; accent: string; ring: string }
> = {
  vehicle_created: { label: 'NEW', accent: 'text-muted', ring: 'border-white/[0.08] bg-[#0a0a0a]' },
  modification_added: { label: 'MOD', accent: 'text-muted', ring: 'border-white/[0.08] bg-[#0a0a0a]' },
  event_registered: { label: 'REG', accent: 'text-muted', ring: 'border-white/[0.08] bg-[#0a0a0a]' },
  event_attended: { label: 'EVT', accent: 'text-muted', ring: 'border-white/[0.08] bg-[#0a0a0a]' },
  award_won: { label: 'WIN', accent: 'text-orange-300/90', ring: 'border-orange-500/40 bg-orange-950/40' },
  milestone: { label: <CtaIcon direction="star" compact />, accent: 'text-orange-300/90', ring: 'border-orange-500/30 bg-orange-950/30' },
  badge_earned: { label: 'BDG', accent: 'text-orange-300/90', ring: 'border-orange-500/30 bg-orange-950/30' },
  club_joined: { label: 'CLB', accent: 'text-muted', ring: 'border-white/[0.08] bg-[#0a0a0a]' },
};

function parseYear(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  return isNaN(d.getTime()) ? 'Unknown' : String(d.getFullYear());
}

function formatMonthDay(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface BuildTimelineProps {
  entries: VehicleHistoryEntry[];
  loading?: boolean;
  className?: string;
}

const BuildTimeline: React.FC<BuildTimelineProps> = ({ entries, loading, className = '' }) => {
  const [workspace, setWorkspace] = useState(false);
  const grouped = useMemo(() => {
    const map = new Map<string, VehicleHistoryEntry[]>();
    for (const entry of entries) {
      const year = parseYear(entry.occurred_at);
      if (!map.has(year)) map.set(year, []);
      map.get(year)!.push(entry);
    }
    return Array.from(map.entries()).sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [entries]);

  // Only skeleton on cold load — never swap filled content for a pulse shell
  // (that collapses height and snaps scroll to the section top).
  if (loading && entries.length === 0) {
    return (
      <section className={`card p-5 sm:p-6 ${className}`}>
        <div className="border-b border-white/[0.06] pb-3 mb-5">
          <div className="h-3 w-24 bg-[#111] rounded animate-pulse mb-2" />
          <div className="h-6 w-40 bg-[#111] rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 bg-[#111]/50 rounded-lg animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (entries.length === 0) {
    return (
      <section className={`card border-dashed p-6 sm:p-8 text-center ${className}`}>
        <h2 className="heading-section text-lg mb-2">Activity</h2>
        <p className="text-muted text-sm max-w-sm mx-auto leading-relaxed">
          Events, clubs, awards, and badges show up here as this build earns recognition.
        </p>
      </section>
    );
  }

  return (
    <section className={`card p-5 sm:p-6 ${className}`}>
      <div className="build-section-intro border-b border-white/[0.06] pb-3 mb-6">
        <p className="kicker mb-2">Timeline</p>
        <h2 className="heading-section text-lg sm:text-xl">Activity</h2>
        <p className="text-meta mt-1">Events, clubs, awards, and recognition</p>
        <button type="button" className="btn-secondary mt-4 min-h-[44px]" onClick={() => setWorkspace(true)}>Open Build Passport workspace</button>
      </div>
      {workspace && <BuildPassportWorkspace entries={entries} onClose={() => setWorkspace(false)} />}

      <div className="space-y-8">
        {grouped.map(([year, yearEntries]) => (
          <div key={year}>
            <p className="font-mono text-xs font-medium text-gray-500 tabular-nums mb-3">{year}</p>
            <ol className="relative border-l border-white/[0.06] ml-2 space-y-3">
              {yearEntries.map((entry) => {
                const meta = TYPE_META[entry.event_type] || TYPE_META.milestone;
                const isAward =
                  entry.event_type === 'award_won' || entry.event_type === 'badge_earned';
                return (
                  <li key={entry.id} className="relative pl-7 sm:pl-8">
                    <span
                      className={`absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full border text-[9px] font-bold tracking-tight ${meta.ring} ${isAward ? meta.accent : 'text-gray-500'}`}
                      aria-hidden
                    >
                      {meta.label}
                    </span>
                    <div
                      className={`rounded-lg border px-4 py-3 ${
                        isAward
                          ? 'border-orange-500/25 bg-orange-950/20'
                          : 'border-white/[0.06] bg-[#0a0a0a]/40'
                      }`}
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <p className="font-medium leading-snug text-white">
                          {entry.title}
                        </p>
                        <time className="font-mono text-[11px] text-gray-500 shrink-0 tabular-nums">
                          {formatMonthDay(entry.occurred_at)}
                        </time>
                      </div>
                      {(entry.description || entry.event_title) && (
                        <p className={`text-sm mt-1 ${isAward ? 'text-orange-300/80' : 'text-muted'}`}>
                          {entry.event_slug && entry.event_title ? (
                            <Link to={`/events/${entry.event_slug}`} className="hover:underline underline-offset-2">
                              {entry.event_title}
                            </Link>
                          ) : (
                            entry.description || entry.event_title
                          )}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </div>
    </section>
  );
};

export default BuildTimeline;
